// services/admin/usersService.ts
import axios, { AxiosResponse } from "axios";
import { UpdateUserResponse, User } from "../../types/features/administration/User";
import { BACKEND_URL } from "../../config/api";

const getCountryInfo = (countryId: number): { name: string; flag: string } => {
  const countryMap: Record<number, { name: string; flag: string }> = {
    1: { name: "Canada", flag: "🇨🇦" },
    2: { name: "Cameroun", flag: "🇨🇲" },
  };
  return countryMap[countryId] || { name: "Inconnu", flag: "🌍" };
};

const getKycStatus = (user: any): "pending" | "approved" | "rejected" | "incomplete" | "not_requested" => {
  if (user.is_verified === "YES" && user.kyc_status === "ACTIVE" && user.has_kyc) {
    return "approved";
  }
  if (user.is_verified === "NO" && user.kyc_status === "ACTIVE" && user.has_kyc) {
    return "pending";
  }
  if (user.is_verified === "NO" && user.kyc_status === "INACTIVE" && user.has_kyc) {
    return "incomplete";
  }
  if (user.kyc_status === "NO_KYC" || !user.has_kyc) {
    return "not_requested";
  }
  return "not_requested";
};

const getRiskLevel = (_user: any): "low" | "medium" | "high" => {
  return "low";
};

const getLastActivity = (updatedAt: string): string => {
  const now = new Date();
  const updated = new Date(updatedAt);
  const diffMs = now.getTime() - updated.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const transformData = (apiUsers: any[]): User[] => {
  console.log(apiUsers.filter((user) => user.interact_email !== null && user.interact_email !== ""));
  return apiUsers.map((user: any) => {
    const countryInfo = getCountryInfo(user.country_id);
    console.log();

    return {
      id: user.id,
      name: `${user.first_name} ${user.last_name}`,
      first_name: `${user.first_name}`,
      last_name: `${user.last_name}`,

      email: user.email,
      country: countryInfo.name,
      countryFlag: countryInfo.flag,
      kycStatus: getKycStatus(user),
      riskLevel: getRiskLevel(user),
      registrationDate: user.created_at?.split("T")[0] || "",
      interact_email: user.interact_email,
      documents: {
        id: false,
        passport: false,
        address: false,
        selfie: false,
      },
      lastActivity: getLastActivity(user.updated_at),
      status: user.status as "ACTIVE" | "INACTIVE",
      phonenumber: user.phonenumber,
      is_verified: user.is_verified as "YES" | "NO",
      connexion_type: user.connexion_type as "EMAIL" | "GOOGLE" | "FACEBOOK" | "PHONE",
      role: user.role as "USER" | "ADMIN" | "MODERATOR",
      country_id: user.country_id,
      created_at: user.created_at,
      updated_at: user.updated_at,
      kyc_status: user.kyc_status,
      has_kyc: user.has_kyc,
      birthdate: user.birthdate,
      expired_document_date: user.expired_document_date,
      transaction_stats: user.transaction_stats,
    };
  });
};

export const usersService = {
  async createUser(user: User): Promise<User> {
    try {
      const response: AxiosResponse<User> = await axios.post(`${BACKEND_URL}/user`, user);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création de l'utilisateur :", error);
      throw error;
    }
  },

  async getUserById(id: string | number, token: string): Promise<User | null> {
    try {
      const response: AxiosResponse<User> = await axios.get(`${BACKEND_URL}/user/user_id?q=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'utilisateur ${id} :`, error);
      return null;
    }
  },

  async remindNoKyC(token: string): Promise<any> {
    try {
      const response: AxiosResponse<User> = await axios.post(
        `${BACKEND_URL}/kyc/remind-no-kyc`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de l'envoi de mails au utilisateurs :`, error);
      return null;
    }
  },

  async searchUsers(token: string, query: string): Promise<User[]> {
    try {
      const response = await axios.get(`${BACKEND_URL}/user/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return transformData(response.data.users || []);
    } catch (error) {
      console.error("Erreur lors de la recherche d'utilisateurs :", error);
      return [];
    }
  },

  async getUsers(token: string): Promise<User[]> {
    try {
      const response = await axios.get<{ message: string; users: any[] }>(`${BACKEND_URL}/user/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return transformData(response.data.users || []);
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs :", error);
      throw error;
    }
  },

  async updateUser(id: string | number, updatedData?: Partial<User>, token?: string): Promise<UpdateUserResponse> {
    try {
      const response: AxiosResponse<UpdateUserResponse> = await axios.put(`${BACKEND_URL}/user/${id}`, updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de l'utilisateur ${id} :`, error);
      throw error;
    }
  },

  async deleteUser(id: string | number): Promise<boolean> {
    try {
      await axios.delete(`${BACKEND_URL}/user/${id}`);
      return true;
    } catch (error) {
      console.error(`Erreur lors de la suppression de l'utilisateur ${id} :`, error);
      throw error;
    }
  },

  async getUserWithKYC(userId: string, token: string): Promise<any> {
    try {
      const response = await axios.get(`${BACKEND_URL}/kyc/user/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du KYC pour l'utilisateur ${userId} :`, error);
      throw error;
    }
  },

  async approveUserKYC(userId: string, token: string): Promise<any> {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/kyc/activate-user?user_id=${userId}&is_verified=YES`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de l'approbation du KYC pour l'utilisateur ${userId} :`, error);
      throw error;
    }
  },

  async rejectUserKYC(userId: string, token: string): Promise<any> {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/kyc/activate-user?user_id=${userId}&is_verified=NO`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur lors du rejet du KYC pour l'utilisateur ${userId} :`, error);
      throw error;
    }
  },

  async rejectUserKYCWithReason(
    userId: string,
    reason: string,
    notificationMethods: string[],
    token: string
  ): Promise<any> {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/kyc/reject-kyc`,
        {
          user_id: userId,
          reason: reason,
          notification_methods: notificationMethods,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur lors du rejet du KYC pour l'utilisateur ${userId} :`, error);
      throw error;
    }
  },

  async updateUserStatus(userId: string, status: "ACTIVE" | "INACTIVE", token: string): Promise<any> {
    try {
      const response = await axios.patch(
        `${BACKEND_URL}/user/${userId}/status`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du statut pour l'utilisateur ${userId} :`, error);
      throw error;
    }
  },

  async getCurrentUser(token: string): Promise<any> {
    try {
      const response = await axios.get(`${BACKEND_URL}/user/get_user_by_token`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.user;
    } catch (error) {
      console.error("Erreur lors de la récupération de l'utilisateur connecté :", error);
      throw error;
    }
  },

  async getUserKYCStatus(userId: string, token: string): Promise<any> {
    try {
      const response = await axios.get(`${BACKEND_URL}/kyc/check-verified?user_id=${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du statut KYC pour l'utilisateur ${userId} :`, error);
      return { kyc_status: "NO_KYC", has_kyc: false, is_verified: "NO" };
    }
  },

  async changeUserCountry(userId: string, newCountryId: number, token: string): Promise<any> {
    try {
      const response = await axios.patch(
        `${BACKEND_URL}/user/${userId}/country`,
        {
          country_id: newCountryId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur lors du changement de pays pour l'utilisateur ${userId} :`, error);
      throw error;
    }
  },

  async getCountryHistory(userId: string, token: string) {
    try {
      const response = await axios.get(`${BACKEND_URL}/user/${userId}/country-history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'historique de pays pour l'utilisateur ${userId} :`, error);
      return error;
    }
  },

  async submitUserKYC(userId: string, payload: FormData, token?: string) {
    try {
      const response = await axios.put(`${BACKEND_URL}/kyc/admin/user/${userId} `, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        // ⚠️ Empêche axios de transformer/sérialiser le FormData
        transformRequest: [(data) => data],
      });
      return response.data;
    } catch (error: any) {
      console.error(`Erreur lors de l'upload du KYC pour l'utilisateur ${userId} :`, error.response);
      throw error;
    }
  },
};
